/**
 * Cookie consent (GDPR / ePrivacy): the visitor's choice lives in a
 * first-party cookie readable by client code, e.g. `cookie_consent=v1.analytics`.
 * Bump the version to ask everyone again after a material change.
 */
export const CONSENT_COOKIE_NAME = "cookie_consent";
export const CONSENT_VERSION = "v1";
export const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/** `analytics`: essential + analytics cookies; `essential`: essential only. */
export type ConsentChoice = "analytics" | "essential";

export function serializeConsent(choice: ConsentChoice): string {
  return `${CONSENT_VERSION}.${choice}`;
}

/** Parse a cookie value; unknown or outdated versions count as no choice. */
export function parseConsent(
  value: string | null | undefined,
): ConsentChoice | null {
  switch (value) {
    case `${CONSENT_VERSION}.analytics`:
      return "analytics";
    case `${CONSENT_VERSION}.essential`:
      return "essential";
    default:
      return null;
  }
}

/** Read the choice from a `document.cookie`-style string. */
export function readConsentFromCookieString(
  cookieString: string | null | undefined,
): ConsentChoice | null {
  for (const part of (cookieString ?? "").split(";")) {
    const [name, ...rest] = part.trim().split("=");

    if (name === CONSENT_COOKIE_NAME) return parseConsent(rest.join("="));
  }

  return null;
}

/** `Set-Cookie`-style attributes for `document.cookie`. */
export function buildConsentCookie(
  choice: ConsentChoice,
  { secure = false }: { secure?: boolean } = {},
): string {
  return [
    `${CONSENT_COOKIE_NAME}=${serializeConsent(choice)}`,
    "Path=/",
    `Max-Age=${CONSENT_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
    ...(secure ? ["Secure"] : []),
  ].join("; ");
}
